import { INaviTableBodyRow } from '../../interfaces';
import styles from './NaviTableBody.module.scss';
import cx from 'classnames';
import { isFunction } from '@cp/src/utils/commonUtils';
import { useNaviTableContext } from '../../context/NaviTableContext';
import NaviTableBodyCell from './NaviTableBodyCell';
import { RowData } from '@tanstack/react-table';
import { useEffect, useRef, useState } from 'react';

const NaviTableBodyRow = <TData extends RowData>({ row }: INaviTableBodyRow<TData>) => {
  const {
    table,
    expandedRowComponent,
    enableRowClick,
    onRowClick,
    bodyRowStyles,
    alternateRowColor
  } = useNaviTableContext<TData>();
  const [expandedRowHeight, setExpandedRowHeight] = useState(0);
  const expandedRowRef = useRef<HTMLDivElement>(null);
  const isRowExpanded = row.getIsExpanded();
  const ExpandedRowComponent = expandedRowComponent?.(row.original);
  const showExpandedRowComponent = ExpandedRowComponent && isRowExpanded;
  const isRowClickable =
    (enableRowClick?.(row.original) && isFunction(onRowClick)) || ExpandedRowComponent;
  const isLastRow = row.index === table.options?.data?.length - 1;
  const isEvenRow = row.index % 2 === 0;
  const alternateRowStyle =
    alternateRowColor && isEvenRow ? { backgroundColor: alternateRowColor } : undefined;

  const handleRowClick = () => {
    if (isRowClickable && isFunction(onRowClick)) {
      onRowClick(row.original);
    }
    if (ExpandedRowComponent) {
      table.resetExpanded();
      row.toggleExpanded(!isRowExpanded);
    }
  };

  useEffect(() => {
    if (isRowExpanded && expandedRowRef.current) {
      setExpandedRowHeight(expandedRowRef.current.scrollHeight);
    } else {
      setExpandedRowHeight(0);
    }
  }, [isRowExpanded]);

  return (
    <>
      <div
        className={cx(
          styles.naviTableBodyRow,
          {
            [styles.naviTableBodyRowClickable]: isRowClickable,
            ['!border-b-0']: isLastRow && !showExpandedRowComponent
          },
          bodyRowStyles?.(row.original)
        )}
        style={alternateRowStyle}
        onClick={handleRowClick}
      >
        {row.getVisibleCells().map(cell => (
          <NaviTableBodyCell key={cell.id} cell={cell} />
        ))}
      </div>
      <div
        className={cx(styles.expandedRow, {
          [styles.expandedRowVisible]: showExpandedRowComponent,
          [styles.expandedRowHidden]: !showExpandedRowComponent
        })}
        ref={expandedRowRef}
        style={{
          height: isRowExpanded ? `${expandedRowHeight}px` : 0
        }}
      >
        {ExpandedRowComponent}
      </div>
    </>
  );
};

export default NaviTableBodyRow;
